import { FormattedMessage } from 'react-intl';

import { Rarity, type Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { iconCart } from '@sorare/core/src/atoms/icons/system/iconCart.ts';
import { iconGavel } from '@sorare/core/src/atoms/icons/system/iconGavel.ts';
import { iconLightningBolt } from '@sorare/core/src/atoms/icons/system/iconLightningBolt.ts';
import { iconTransfer } from '@sorare/core/src/atoms/icons/system/iconTransfer.ts';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
  ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useMatchdayPicksTab } from '@sorare/core/src/hooks/market/useMatchdayPicksTab';
import { navLabels, transferMarket } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import type { MarketTab } from '#src/components/market/MarketTabBar/index.tsx';
import { MarketTabBar } from '#src/components/market/MarketTabBar/index.tsx';
import { getMarketShopUrl } from '#src/lib/marketShopRoutes.ts';

type Props = {
  rarity: Rarity;
  sport: Sport;
};

export const MarketShopTabs = ({ rarity, sport }: Props) => {
  const { show: showMatchdayPicks } = useMatchdayPicksTab({ sport });

  if ([Rarity.custom_series, Rarity.common].includesValue(rarity)) {
    return null;
  }

  const tabs: MarketTab[] = [
    ...(showMatchdayPicks
      ? [
          {
            to: generateSportPath(ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS, {
              sport,
            }),
            icon: iconLightningBolt,
            iconColor: 'var(--c-red-400)',
            label: (
              <Button size="compact" color="red" asSpan>
                <FormattedMessage defaultMessage="Flash sales" />
              </Button>
            ),
            alwaysShowLabel: true,
          },
        ]
      : []),
    {
      to: getMarketShopUrl(ANY_SPORT_MARKET_SHOP_AUCTIONS, { sport, rarity }),
      icon: iconGavel,
      label: <FormattedMessage {...navLabels.auctions} />,
    },
    {
      to: getMarketShopUrl(ANY_SPORT_MARKET_SHOP_INSTANT_BUY, {
        sport,
        rarity,
      }),
      icon: iconCart,
      label: <FormattedMessage {...transferMarket.instantBuy} />,
    },
    {
      to: getMarketShopUrl(ANY_SPORT_MARKET_SHOP_MANAGER_SALES, {
        sport,
        rarity,
      }),
      icon: iconTransfer,
      label: <FormattedMessage {...transferMarket.transfer} />,
    },
  ];

  return <MarketTabBar tabs={tabs} />;
};
